import { Injectable } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, tap } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { PlatformService } from '@app/services/platform/platform.service';
import { SanitizationService } from '@app/services/sanitization/sanitization.service';
import { StorageService } from '@app/services/storage/storage.service';

/**
 * Storage key holding the pending return URL. LogoutService clears it on a 'user-initiated'
 * sign-out only; a 'session-ended' one leaves it, since the user is about to sign back in.
 */
export const RETURN_URL_KEY = 'pendingReturnUrl';

/**
 * How long a stashed route stays valid.
 *
 * It only needs to survive one sign-in round trip. Nothing clears it when the user simply does
 * not finish signing in, and it lives in persistent storage — so without a window an abandoned
 * consent link fires days later on an unrelated sign-in, sending someone who opened the app to
 * print into a Microsoft consent screen. Losing a stash is cheap by comparison: the e-mailed
 * link carries no one-time token and can be followed again.
 */
export const RETURN_URL_MAX_AGE_MS = 15 * 60 * 1000;

// What actually goes into storage. Both platform implementations JSON round-trip the value,
// so an object survives on native (SQLite) as well as on web (localStorage).
interface IStashedReturnUrl {
  url: string;
  storedAt: number;
}

// Carries "was there anything to clear?" alongside the value, so the clear decision does
// not depend on whether the value happened to survive validation.
interface IConsumedReturnUrl {
  url: string | null;
  wasStored: boolean;
}

/**
 * Remembers the route a user was heading for when the auth guard interrupted them, so they
 * are returned there after signing in. Stored rather than held in memory because web
 * sign-in leaves the app and re-bootstraps it. One-shot: {@link consume} clears as it reads,
 * and a stash the user never came back for expires — see {@link RETURN_URL_MAX_AGE_MS}.
 */
@Injectable({
  providedIn: 'root'
})
export class ReturnUrlService {

  private logger: Logger;

  constructor(
    private loggingService: LoggingService,
    private platformService: PlatformService,
    private sanitizationService: SanitizationService,
    private storageService: StorageService
  ) {
    this.logger = loggingService.getLogger('[ReturnUrlService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
  }

  // ############### PUBLIC API ###############

  /**
   * Remembers the attempted URL. Fire-and-forget: callers are synchronous and the value is
   * only needed after sign-in. Invalid routes are dropped rather than stored.
   *
   * @param url - The attempted URL, typically RouterStateSnapshot.url
   */
  public stash(url: string | null | undefined): void {
    const methodName = 'stash()';
    const validated = this.sanitizationService.validateInternalReturnUrl(url);
    if (!validated) {
      if (url) {
        // Warn, not info: refusing to remember a route the user asked for is the failure this
        // service exists to prevent, and it is otherwise invisible. Auth routes are refused by
        // design and warn too.
        this.logger.warn(methodName + ' refused to remember url=' +
          this.sanitizationService.sanitizeLogOutput(url));
      } else {
        this.logger.debug(methodName + ' nothing to remember');
      }
      return;
    }
    this.logger.info(methodName, 'Remembering interrupted route');
    // Stamped at write time, so the age is measured from when the user was interrupted.
    const stashed: IStashedReturnUrl = { url: validated, storedAt: Date.now() };
    // No error handler: StorageService.addItemToLocalStorage() catches both platform
    // implementations and emits success regardless, so there is nothing here to catch. What
    // it cannot paper over is the write never being attempted — see whenWritable().
    this.whenWritable(methodName)
      .pipe(
        switchMap(() => this.storageService.addItemToLocalStorage(RETURN_URL_KEY, stashed, methodName)),
        take(1)
      )
      .subscribe();
  }

  /**
   * Returns the remembered URL and clears it, or null when there is none. Re-validated on
   * read, as the route list may have changed between app versions.
   *
   * One-shot on every path: a value that is read, a value that no longer validates, a value
   * that has expired, and a read that fails outright all clear the stash. Anything left
   * behind would fire on a later, unrelated sign-in.
   *
   * @returns The validated return URL, or null
   */
  public consume(): Observable<string | null> {
    const methodName = 'consume()';
    return this.storageService.getItemFromLocalStorage(RETURN_URL_KEY, methodName).pipe(
      take(1),
      map((stored: unknown): IConsumedReturnUrl => {
        const candidate = this.readFreshUrl(stored, methodName);
        const validated = this.sanitizationService.validateInternalReturnUrl(candidate);
        if (candidate && !validated) {
          this.logger.warn(methodName + ' discarded stored return URL that no longer validates');
        }
        return { url: validated, wasStored: !!stored };
      }),
      // Ahead of the clear, so a failed read still clears: a value we could not read is
      // exactly the kind that would resurface on a later sign-in.
      catchError((): Observable<IConsumedReturnUrl> => {
        this.logger.warn(methodName + ' could not read the stored return URL');
        return of({ url: null, wasStored: true });
      }),
      // Deliberately not chained ahead of the emission: this is on the post-sign-in
      // navigation path, and a delete that never completes would strand the user on a
      // blank page. Skipped when there was nothing stored, to avoid a pointless write on
      // every sign-in.
      tap((result: IConsumedReturnUrl) => {
        if (result.wasStored) {
          this.clearAfterConsume(methodName);
        }
      }),
      map((result: IConsumedReturnUrl) => result.url)
    );
  }

  /**
   * Drops any remembered URL without navigating to it, e.g. on sign-out. Retries once, as
   * the underlying store is SQLite on native and a transient failure would otherwise leave
   * the stash live.
   *
   * @returns True when the stash is gone, false when it could not be removed
   */
  public clear(): Observable<boolean> {
    const methodName = 'clear()';
    // defer(), so retry() re-invokes removeStoredItems() rather than merely re-subscribing
    // the observable it already returned — on web the localStorage removal happens eagerly
    // inside that call, so a bare retry would repeat only half the work.
    return defer(() => this.storageService.removeStoredItems([RETURN_URL_KEY])).pipe(
      retry(1),
      take(1),
      map(() => true),
      catchError(() => {
        this.logger.warn(methodName + ' could not clear the return URL');
        return of(false);
      })
    );
  }

  // ############### INTERNAL ###############

  /**
   * Unwraps a stored value and returns its URL only while it is still fresh.
   *
   * Anything that is not the shape {@link stash} writes is treated as absent rather than
   * trusted: a value with no timestamp cannot be aged.
   *
   * @param stored - Whatever came back from storage
   * @param fromMethod - Caller name, for logging
   * @returns The stashed URL while unexpired, otherwise null
   */
  private readFreshUrl(stored: unknown, fromMethod: string): string | null {
    if (!stored) {
      return null;
    }
    const stash = stored as Partial<IStashedReturnUrl>;
    if (typeof stash.url !== 'string' || !Number.isFinite(stash.storedAt)) {
      this.logger.warn(fromMethod + ' discarded stored return URL of an unrecognised shape');
      return null;
    }
    // Ages forward only. A device clock moved backwards makes this negative, which reads as
    // fresh — harmless, because the value is consumed on this very sign-in either way.
    const age = Date.now() - (stash.storedAt as number);
    if (age > RETURN_URL_MAX_AGE_MS) {
      this.logger.info(fromMethod, 'Discarded a return URL older than the window, age(ms): ' + age);
      return null;
    }
    return stash.url;
  }

  /**
   * Waits for storage to be able to accept a write.
   *
   * On Cordova the SQLite DB is initialised asynchronously (StorageService.init →
   * platform.ready → createDB → dbReady) and `executeQuery()` errors outright while `db` is
   * still null. StorageService then swallows that error and reports success, so an ungated
   * stash on a native cold start would log "Remembering interrupted route", write nothing,
   * and leave `consume()` returning null — silently dropping the user on the default page,
   * which is the exact failure this service exists to prevent. Same gate and same reason as
   * `TenantService.initializeTenantListLength()` (BUG-942828 / BUG-1011694).
   *
   * Only the write is gated. Deferring it is free, because `stash()` is fire-and-forget and
   * the value is not needed until sign-in completes. `consume()` is deliberately *not*
   * gated: it sits on the post-sign-in navigation path, so waiting on a DB that never
   * becomes ready would strand the user on a blank page — whereas an errored read already
   * degrades gracefully to the default landing page.
   *
   * @param fromMethod - Caller name, for logging
   * @returns An observable that emits once when a write can be attempted
   */
  private whenWritable(fromMethod: string): Observable<boolean> {
    if (!this.platformService.isCordova()) {
      return of(true);
    }
    this.logger.debug(fromMethod + ' waiting for the native database');
    return this.storageService.getDatabaseState().pipe(
      filter((isReady: boolean) => isReady),
      take(1)
    );
  }

  // Fire-and-forget, but never silent: a stash that survives its own consumption is the
  // one thing this service must not do quietly.
  private clearAfterConsume(fromMethod: string): void {
    this.clear().pipe(take(1)).subscribe((cleared: boolean) => {
      if (!cleared) {
        this.logger.warn(fromMethod + ' return URL was consumed but could not be cleared - ' +
          'it may fire again on the next sign-in');
      }
    });
  }
}
